(ns arthur.domain.project
  "A clip <-> the document that travels. The tier split, as a pair of functions.

  `save` takes what `flow/freeze` produced — `{:clip ... :store ...}` — and
  returns two things that are allowed on the wire for different reasons:

    :leaves   TIER 1. The document. Timelines, nodes, channels, subjects,
              features, groups, time maps, the analysis record. Kilobytes, and
              every byte of it authored or authorable.

    :blocks   TIER 2. The dense blocks the document NAMES, each with the
              descriptor its key is the hash of. Megabytes, content-addressed,
              and not part of the document — a bake inside the shared document is
              a system that puts 48KB on the wire per vertex drag.

  ONLY WHAT THE DOCUMENT NAMES TRAVELS. The blocks are selected by walking the
  leaves for dense store keys, not by taking the store wholesale, so a store that
  has accumulated a block nothing points at does not upload it. That is also the
  check that the split is honest: if a channel named a block the store did not
  have, `save` would say so here rather than producing a document that loads into
  a blank stage somewhere else.

  THE BYTES ARE NOT IN THE DOCUMENT AND THE TYPE IS NOT IN THE BYTES. A block's
  element type comes out of its own descriptor, which is the only place it is
  written down: an Int16Array and a Float32Array over the same bytes are both
  valid readings of them, and only one is the block. That makes the descriptor
  load-bearing rather than documentation, which is the right way round for the
  thing a key is the hash of."
  (:require [arthur.domain.leaf :as leaf]
            [arthur.domain.wire :as wire]))

(def schema-version
  "The stored document format. A client refuses a project stored under a
  different one — see `events/project` — so this is bumped by any change to what
  a leaf may contain.

  8 added `[:xform :pivot]`, the point a node turns and scales about, which 7 had
  deleted as `[:xform :anchor]` on the theory that a peg could stand in for one.
  It cannot: a peg is a node and a turn about one is still `pos` solved per
  frame, so a KEYED turn of anything whose origin was not its own middle orbited
  that origin. See `node/xform-paths` and docs/animation-model.md.

  CONVERTED, AND THE ONLY VERSION SO FAR THAT IS, because this one is exact: a
  schema-7 node has no pivot, an absent pivot reads as `[0 0]` off
  `node/defaults`, and `T(pos)·T(0)·M·T(-0)` is `T(pos)·M` to the bit. So every
  stored document composes to the same matrices it did, and the migration only
  restamps the version — `clips/migrations/0017`. That is the difference from 7,
  which could not convert an anchor it had deleted: dropping one moved anything
  since turned or scaled by hand, and tier-2 positions cannot be rewritten at
  all. Adding a component with an identity default takes nothing away.

  A pivot a schema-7 document never got to choose is still unchosen, and the
  first turn or scale of such a node writes one — `gesture/with-pivot` — so the
  conversion does not have to guess where anybody wanted it."
  8)

(defn block-keys
  "Every tier-2 key a leaf map names, in a stable order."
  [leaves]
  (->> (vals leaves)
       (keep (comp :store :dense))
       distinct
       sort
       vec))

(defn- block-type
  "A block's element type, out of its descriptor."
  [descriptor]
  (or (get-in (js->clj (js/JSON.parse descriptor)) ["layout" "type"])
      (throw (ex-info "a block's descriptor does not say what its elements are"
                      {:descriptor descriptor}))))

(defn save
  "One clip -> the JS object a save PUTs, ready for `JSON.stringify`.

  A JS object rather than CLJS data, and `load` takes one back, because this is
  the wire boundary and both ends of it should speak the wire: a test can then
  round-trip a clip through `JSON.parse(JSON.stringify(...))` and be running the
  same conversion the network runs, rather than a CLJS-shaped rehearsal of it. The
  one thing a keywordising `js->clj` would quietly break is the leaf paths —
  `:clip/c1/symbol/main/node/mouth` is a keyword whose `name` is
  \"c1/symbol/main/node/mouth\", so the
  \"clip/\" would be lost on the way back in.

  Refuses a document `domain/leaf` calls unaddressable, which is where a hand-made
  clip with placeholder store keys — `demo/swarm`'s \"swarm/pos\" — stops rather
  than being uploaded as a project that means something only on the machine that
  made it."
  [cid {:keys [clip store]}]
  (let [leaves (leaf/leaves cid clip)
        ps     (leaf/problems leaves)]
    (when (seq ps)
      (throw (ex-info (str "this clip cannot be saved: " (first ps))
                      {:problems ps})))
    (let [out (js-obj)]
      (doseq [[path v] leaves]
        (aset out path (wire/encode-json v)))
      #js {:leaves out
           :blocks (into-array
                    (map (fn [k]
                           (let [{:keys [data state descriptor]}
                                 (or (get store k)
                                     (throw (ex-info "the document names a block the store does not have"
                                                     {:key k})))]
                             #js {:key k
                                  :descriptor descriptor
                                  :data (wire/base64 data)
                                  :state (when state (wire/base64 state))}))
                         (block-keys leaves)))})))

(defn tier1
  "A response's leaves object -> `{path value}`, the shape `leaf/leaves` returns."
  [^js leaves]
  (into {} (map (fn [path] [path (wire/decode-json (aget leaves path))]))
        (js-keys leaves)))

(defn store
  "Fetched blocks -> the store `save` reads them back out of."
  [blocks]
  (into {}
        (map (fn [^js b]
               [(.-key b)
                (cond-> {:descriptor (.-descriptor b)
                         :data (wire/typed (block-type (.-descriptor b))
                                           (.-data b))}
                  (.-state b) (assoc :state (wire/bytes-of (.-state b))))]))
        (array-seq (or blocks #js []))))

(defn load
  "The parsed response -> `{:clip :store}`, which is what `flow/freeze` returns
  and therefore what the player already knows how to play."
  [cid ^js doc]
  {:clip (leaf/clip cid (tier1 (.-leaves doc)))
   :store (store (.-blocks doc))})
